import { useId, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { inspectId } from "../lib/uuid";

// Deterministic v7 default so the embedded timestamp renders server-side.
const DEFAULT_ID = "0192f0e1-c000-7abc-8def-0123456789ab";

function DetailRow({
  label,
  value,
  testid,
}: {
  label: string;
  value: string;
  testid: string;
}) {
  return (
    <div className="flex flex-col gap-1 border-b border-border/60 py-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd
        data-testid={testid}
        className="break-all font-mono text-sm text-foreground sm:text-right"
      >
        {value}
      </dd>
    </div>
  );
}

export function UuidTool() {
  const uid = useId();
  const [value, setValue] = useState(DEFAULT_ID);

  const result = useMemo(() => inspectId(value), [value]);

  const rows: Array<{
    id: string;
    label: string;
    value: string;
    testid: string;
  }> = [];
  if (result.ok && result.kind === "uuid") {
    rows.push(
      {
        id: "kind",
        label: m.ud_field_kind(),
        value: m.ud_kind_uuid(),
        testid: "id-kind",
      },
      {
        id: "version",
        label: m.ud_field_version(),
        value: result.versionLabel,
        testid: "id-version",
      },
      {
        id: "variant",
        label: m.ud_field_variant(),
        value: result.variant,
        testid: "id-variant",
      },
      {
        id: "timestamp",
        label: m.ud_field_timestamp(),
        value: result.timestamp ?? m.ud_none(),
        testid: "id-timestamp",
      },
      {
        id: "normalized",
        label: m.ud_field_normalized(),
        value: result.normalized,
        testid: "id-normalized",
      },
    );
  } else if (result.ok && result.kind === "ulid") {
    rows.push(
      {
        id: "kind",
        label: m.ud_field_kind(),
        value: m.ud_kind_ulid(),
        testid: "id-kind",
      },
      {
        id: "timestamp",
        label: m.ud_field_timestamp(),
        value: result.timestamp,
        testid: "id-timestamp",
      },
      {
        id: "normalized",
        label: m.ud_field_normalized(),
        value: result.normalized,
        testid: "id-normalized",
      },
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <Label htmlFor={`${uid}-id`}>{m.ud_input_label()}</Label>
        <Input
          id={`${uid}-id`}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          className="font-mono text-base"
        />
      </div>

      <div className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        {result.ok ? (
          <dl>
            {rows.map((row) => (
              <DetailRow
                key={row.id}
                label={row.label}
                value={row.value}
                testid={row.testid}
              />
            ))}
          </dl>
        ) : (
          <p data-testid="id-error" className="text-sm text-destructive">
            {result.error}
          </p>
        )}
      </div>
    </div>
  );
}
